import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, StartLayout } from "@sequentech/ui-essentials";
import { stringToHtml, translateFromPresentation, EStartScreenTitlePolicy, EElectionEventContestEncryptionPolicy, EDeclineToVotePolicy, areAllContestsAcclaimed, } from "@sequentech/ui-core";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import StartActions from "../components/StartActions/StartActions";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { selectElectionById } from "../store/elections/electionsSlice";
import { CircularProgress } from "@mui/material";
import { useRootBackLink } from "../hooks/root-back-link";
import Stepper from "../components/Stepper";
import { selectBallotStyleByElectionId, showDemo } from "../store/ballotStyles/ballotStylesSlice";
import { selectElectionEventById } from "../store/electionEvents/electionEventsSlice";
import { resetBallotSelection, selectBallotSelectionByElectionId, setAllBallotSelectionsDeclineToVote, } from "../store/ballotSelections/ballotSelectionsSlice";
import { clearIsVoted, setDeclinedToVote, setIsVoted } from "../store/extra/extraSlice";
import { useEncryptBallotForReview } from "../hooks/useEncryptBallotForReview";
import { store } from "../store/store";
var StartScreen = function () {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var _j = useTranslation(), t = _j.t, i18n = _j.i18n;
    var electionId = useParams().electionId;
    var election = useAppSelector(selectElectionById(String(electionId)));
    var _k = useParams(), eventId = _k.eventId, tenantId = _k.tenantId;
    var electionEvent = useAppSelector(selectElectionEventById(eventId));
    var ballotStyle = useAppSelector(selectBallotStyleByElectionId(String(electionId)));
    var backLink = useRootBackLink();
    var isDemo = useAppSelector(showDemo(electionId));
    var _l = useState(isDemo), showDemoDialog = _l[0], setShowDemoDialog = _l[1];
    var _m = useState(false), openDeclineDialog = _m[0], setOpenDeclineDialog = _m[1];
    var dispatch = useAppDispatch();
    var navigate = useNavigate();
    var location = useLocation();
    var encryptAndStoreBallot = useEncryptBallotForReview().encryptAndStoreBallot;
    var titleObject = useMemo(function () {
        var _a;
        var startScreenTitlePolicy = (_a = election === null || election === void 0 ? void 0 : election.presentation) === null || _a === void 0 ? void 0 : _a.start_screen_title_policy;
        return startScreenTitlePolicy === EStartScreenTitlePolicy.ELECTION_EVENT
            ? electionEvent
            : election;
    }, [election, electionEvent]);
    var defaultLanguageCode = (_c = (_b = (_a = titleObject === null || titleObject === void 0 ? void 0 : titleObject.presentation) === null || _a === void 0 ? void 0 : _a.language_conf) === null || _b === void 0 ? void 0 : _b.default_language_code) !== null && _c !== void 0 ? _c : (_e = (_d = electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation) === null || _d === void 0 ? void 0 : _d.language_conf) === null || _e === void 0 ? void 0 : _e.default_language_code;
    var titleDescription = translateFromPresentation(titleObject, "description", i18n.language, {
        defaultLanguageCode: defaultLanguageCode,
    });
    useEffect(function () {
        if (!election || !titleObject) {
            navigate(backLink);
        }
    });
    useEffect(function () {
        if (!ballotStyle) {
            return;
        }
        dispatch(resetBallotSelection({
            ballotStyle: ballotStyle,
            force: true,
        }));
        dispatch(clearIsVoted());
    }, [ballotStyle]);
    var declineToVotePolicy = (_f = election === null || election === void 0 ? void 0 : election.presentation) === null || _f === void 0 ? void 0 : _f.decline_to_vote_policy;
    var isMultiContest = ((_g = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml.election_event_presentation) === null || _g === void 0 ? void 0 : _g.contest_encryption_policy) ===
        EElectionEventContestEncryptionPolicy.MULTIPLE_CONTESTS;
    // Declining is casting a ballot, and a fully acclaimed election has no
    // ballot to cast.
    var isDeclineToVotePolicyEnabled = declineToVotePolicy === EDeclineToVotePolicy.ENABLED &&
        isMultiContest &&
        !areAllContestsAcclaimed(ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml.contests);
    var confirmDeclineToVote = function () {
        if (!ballotStyle || !election) {
            return;
        }
        setOpenDeclineDialog(false);
        dispatch(setAllBallotSelectionsDeclineToVote({ ballotStyle: ballotStyle }));
        dispatch(setDeclinedToVote(ballotStyle.election_id));
        dispatch(setIsVoted(ballotStyle.election_id));
        var declinedSelection = selectBallotSelectionByElectionId(ballotStyle.election_id)(store.getState());
        if (!declinedSelection) {
            return;
        }
        if (encryptAndStoreBallot(ballotStyle, declinedSelection, isMultiContest)) {
            navigate("/tenant/".concat(tenantId, "/event/").concat(eventId, "/election/").concat(election.id, "/review").concat(location.search));
        }
    };
    if (!election || !titleObject) {
        return _jsx(CircularProgress, { className: "start-progress", "aria-label": t("a11y.loading") });
    }
    return (_jsxs(StartLayout, { title: (_h = translateFromPresentation(titleObject, "name", i18n.language, {
            defaultLanguageCode: defaultLanguageCode,
        })) !== null && _h !== void 0 ? _h : "-", description: titleDescription ? stringToHtml(titleDescription) : undefined, steps: _jsx(Stepper, { selected: 1 }), below: _jsx(StartActions, { election: election, isDeclineToVotePolicyEnabled: isDeclineToVotePolicyEnabled, onDeclineToVoteClick: function () { return setOpenDeclineDialog(true); } }), children: [_jsx(Dialog, { variant: "warning", open: showDemoDialog, ok: t("electionSelectionScreen.demoDialog.ok"), title: t("electionSelectionScreen.demoDialog.title"), handleClose: function () {
                    setShowDemoDialog(false);
                }, fullWidth: true, className: "demo-dialog", children: stringToHtml(t("electionSelectionScreen.demoDialog.content")) }), isDeclineToVotePolicyEnabled ? (_jsx(Dialog, { className: "decline-to-vote-dialog", handleClose: function (confirmed) {
                    setOpenDeclineDialog(false);
                    if (confirmed) {
                        confirmDeclineToVote();
                    }
                }, open: openDeclineDialog, title: t("startScreen.declineToVoteDialog.title"), ok: t("startScreen.declineToVoteDialog.continue"), cancel: t("startScreen.declineToVoteDialog.cancel"), variant: "info", children: stringToHtml(t("startScreen.declineToVoteDialog.content")) })) : null] }));
};
export default StartScreen;
